/**
 * PDF 渲染层：用 PDFium（Chrome 自己的 PDF 引擎，编译成 WASM）。
 *
 * 为什么不用 pdf.js：工程图纸大多是扫描件，图像编码是 JPEG 2000 / 传真（CCITT），
 * pdf.js 5.x 解这些要走 WASM，而且解码发生在 Web Worker 里——worker 的请求
 * 不保证带登录 Cookie（我们的媒体接口是要鉴权的），结果是图像取不到、被静默跳过，
 * 页面上只剩矢量文字。PDFium 在**主线程**用一次 fetch（带 Cookie）拿到字节后本地解码，
 * 没有 worker，也没有额外的 wasm 路径问题。
 */
import { type PDFiumDocument, PDFiumLibrary } from "@hyzyla/pdfium";
// 浏览器环境必须显式提供 wasm 地址；用 Vite 处理后的资源 URL（自托管，不依赖 CDN）
import pdfiumWasmUrl from "@hyzyla/pdfium/dist/pdfium.wasm?url";

export type PageSize = { page_index: number; width: number; height: number; rotation: number };

let libraryPromise: Promise<PDFiumLibrary> | null = null;
const documentCache = new Map<string, Promise<PDFiumDocument>>();

/** PDFium 只需要初始化一次（wasm 实例），之后复用。 */
function getLibrary(): Promise<PDFiumLibrary> {
  if (!libraryPromise) {
    libraryPromise = PDFiumLibrary.init({ wasmUrl: pdfiumWasmUrl });
    libraryPromise.catch(() => {
      libraryPromise = null;
    });
  }
  return libraryPromise;
}

/**
 * 拿图纸字节：主线程 fetch —— 会带上会话 Cookie，所以鉴权接口也能取到。
 * 同一份图纸（R2 里按 sha256 命名）缓存住，不重复下载。
 */
const bytesCache = new Map<string, Promise<Uint8Array>>();
function fetchBytes(url: string): Promise<Uint8Array> {
  let cached = bytesCache.get(url);
  if (!cached) {
    cached = fetch(url, { credentials: "same-origin" }).then(async (response) => {
      if (!response.ok) throw new Error(`图纸加载失败（${response.status}）`);
      return new Uint8Array(await response.arrayBuffer());
    });
    cached.catch(() => bytesCache.delete(url));
    bytesCache.set(url, cached);
  }
  return cached;
}

export async function loadPdfDocument(url: string): Promise<PDFiumDocument> {
  let cached = documentCache.get(url);
  if (!cached) {
    cached = (async () => {
      const [library, bytes] = await Promise.all([getLibrary(), fetchBytes(url)]);
      return library.loadDocument(bytes);
    })();
    cached.catch(() => documentCache.delete(url));
    documentCache.set(url, cached);
  }
  return cached;
}

export async function getPageCount(url: string): Promise<number> {
  const document = await loadPdfDocument(url);
  return document.getPageCount();
}

/** 读取每页尺寸（单位：pt，1/72 英寸）；上传时回写给服务端。 */
export async function readPageSizes(data: ArrayBuffer): Promise<PageSize[]> {
  const library = await getLibrary();
  const document = await library.loadDocument(new Uint8Array(data));
  try {
    const sizes: PageSize[] = [];
    for (let index = 0; index < document.getPageCount(); index += 1) {
      const { originalWidth, originalHeight } = document.getPage(index).getOriginalSize();
      sizes.push({ page_index: index, width: originalWidth, height: originalHeight, rotation: 0 });
    }
    return sizes;
  } finally {
    document.destroy();
  }
}

export type RenderedPage = { pageCount: number; cssWidth: number; cssHeight: number };

/**
 * PDFium 渲染结果 → ImageData。
 *
 * 坑：`colorSpace: "BGRA"` 说的是给 PDFium 的**位图格式**，库在渲染时带了
 * `FPDFRenderFlag.REVERSE_BYTE_ORDER`，吐出来的字节序其实已经是 RGBA，正好是
 * ImageData 要的顺序，直接用就行。早先这里按名字又手工交换了一次 R/B，等于把红蓝
 * 通道对调：灰度/黑白图纸（R=G=B）看不出来，彩色图纸会整片偏色——实测棕色木纹
 * (158,121,88) 会渲染成 (88,121,158) 的蓝色。
 */
function toImageData(bitmap: { width: number; height: number; data: Uint8Array }): ImageData {
  // 复制一份：ImageData 需要自己持有 ArrayBuffer 的 Uint8ClampedArray
  return new ImageData(new Uint8ClampedArray(bitmap.data), bitmap.width, bitmap.height);
}

/** 把某一页渲染进 canvas（按 CSS 宽度自适应，按设备像素比放大保证清晰）。 */
export async function renderPageToCanvas(options: {
  url: string;
  pageIndex: number;
  canvas: HTMLCanvasElement;
  cssWidth: number;
  devicePixelRatio?: number;
}): Promise<RenderedPage> {
  const document = await loadPdfDocument(options.url);
  const page = document.getPage(options.pageIndex);
  const { originalWidth, originalHeight } = page.getOriginalSize();
  // PDFium 的 scale 是相对 72dpi 的倍数；限制一下上限，避免超大图把内存打爆
  const ratio = Math.min(options.devicePixelRatio ?? 1, 2);
  const targetWidth = Math.min(options.cssWidth * ratio, 4000);
  const scale = targetWidth / originalWidth;

  const bitmap = await page.render({ scale, colorSpace: "BGRA" });

  const canvas = options.canvas;
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("画布不可用");
  context.putImageData(toImageData(bitmap), 0, 0);

  const cssWidth = bitmap.width / ratio;
  return {
    pageCount: document.getPageCount(),
    cssWidth,
    cssHeight: (cssWidth * originalHeight) / originalWidth,
  };
}

/** 页面导航用的缩略图（小尺寸，返回 dataURL）。 */
export async function renderThumbnail(url: string, pageIndex: number, width = 132): Promise<string> {
  const document = await loadPdfDocument(url);
  const page = document.getPage(pageIndex);
  const { originalWidth } = page.getOriginalSize();
  const bitmap = await page.render({ scale: width / originalWidth, colorSpace: "BGRA" });

  const canvas = window.document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("画布不可用");
  context.putImageData(toImageData(bitmap), 0, 0);
  return canvas.toDataURL("image/jpeg", 0.7);
}
